import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

const PINNED_ITEMS_KEY = 'v8.pinnedItems';
const MAX_PINNED = 8;
const ANON_USER = '';

@Injectable({
	providedIn: 'root'
})
export class PinnedMenuService {
	private userid: string = ANON_USER;
	private pinnedItems$ = new BehaviorSubject<string[]>(this.loadPinnedItems(ANON_USER));

	get pinnedItems(): string[] {
		return this.pinnedItems$.getValue();
	}

	/**
	 * Pins are a per-user preference on a workstation several people log into,
	 * so they're stored as a { userid: ids } map under one key — the same shape
	 * printSettings uses, and the same reason it survives logout. Call this once
	 * the user resolves. Pins toggled before we knew who they were (or written
	 * by an older build as a flat array) are adopted into that user's bucket.
	 */
	setUser(userid: string | null | undefined): void {
		const next = String(userid ?? ANON_USER);
		if (next === this.userid) { return; }

		const all = this.readAll();
		const pending = all[ANON_USER] || [];
		this.userid = next;

		if (next !== ANON_USER && pending.length && !(all[next] || []).length) {
			all[next] = pending;
			delete all[ANON_USER];
			this.writeAll(all);
		}

		this.pinnedItems$.next(this.loadPinnedItems(next));
	}

	private readAll(): Record<string, string[]> {
		try {
			const stored = localStorage.getItem(PINNED_ITEMS_KEY);
			if (!stored) { return {}; }
			const parsed = JSON.parse(stored);
			// Legacy format: a flat array written before pins were per-user.
			if (Array.isArray(parsed)) { return { [ANON_USER]: parsed }; }
			return (parsed && typeof parsed === 'object') ? parsed : {};
		} catch {
			return {};
		}
	}

	private writeAll(all: Record<string, string[]>): void {
		try {
			localStorage.setItem(PINNED_ITEMS_KEY, JSON.stringify(all));
		} catch {
			// ignore
		}
	}

	private loadPinnedItems(userid: string): string[] {
		const list = this.readAll()[userid];
		return Array.isArray(list) ? list : [];
	}

	private savePinnedItems(items: string[]): void {
		const all = this.readAll();
		all[this.userid] = items;
		this.writeAll(all);
	}

	togglePin(itemId: string): void {
		if (itemId === 'logout') return;
		const items = this.pinnedItems$.getValue();
		const index = items.indexOf(itemId);
		if (index > -1) {
			const updated = items.filter(id => id !== itemId);
			this.savePinnedItems(updated);
			this.pinnedItems$.next(updated);
			return;
		}
		if (items.length >= MAX_PINNED) return;
		const updated = [...items, itemId];
		this.savePinnedItems(updated);
		this.pinnedItems$.next(updated);
	}

	subscribe(fn: (ids: string[]) => void): { unsubscribe: () => void } {
		const sub = this.pinnedItems$.subscribe(fn);
		return { unsubscribe: () => sub.unsubscribe() };
	}

	clearPinned(): void {
		this.pinnedItems$.next([]);
		this.savePinnedItems([]);
	}
}
